<template>
  <div class="row" v-if="showAttachmentsView">
    <div class="col-12 attachments">
      <div class="d-flex justify-content-between align-items-center" v-if="header">
        <div v-if="filteredAttachments.length" class="label mt-2 mb-2">{{ header }}</div>
        <div class="downloadAllButton" @click="downloadAll" v-if="!disableDownloadAll">
          <i class="far fa-download"></i>
          Download all
        </div>
      </div>
      <div
        class="d-flex align-items-center attachment"
        v-for="attachment in filteredAttachments"
        :key="attachment.id"
        @click="downloadFile(attachment)"
        v-viewer="vvieweroptions"
      >
        <div class="attachment-thumbnail">
          <img
            class="img-fluid"
            style="max-height: 100%"
            :src="apiUrl + '/Attachment/thumbnail/' + attachment.id"
            :data-src="apiUrl + '/Attachment/attachment/' + attachment.id"
            v-if="imageFileExtensions.indexOf(attachment.fileExtension) !== -1"
          />
          <span v-else>{{ attachment.fileExtension.toUpperCase().replace('.', '') }}</span>
        </div>
        <div>
          <div>{{ attachment.fileName }}</div>
          <small>
            Added by {{ attachment.userFullName }} at
            {{ attachment.createdDate | formatDate }}
          </small>
          <div v-if="attachment.isChildItem">
            <small>
              Attachment from linked item -
              <router-link
                :to="{
                  name: 'BoardItemDetail',
                  params: { itemid: attachment.parentId },
                }"
              >
                {{ attachment.parentTitle }}
              </router-link>
            </small>
          </div>
        </div>
        <div class="delete-attachment" v-if="!readOnly">
          <div
            class="badgebutton active mr-2"
            v-b-tooltip:hover
            title="Display inline"
            v-on:click.stop="moveAttachmentToInline(attachment)"
            v-if="canBeDisplayedInline(attachment)"
          >
            <i class="far fa-arrow-alt-up"></i>
          </div>

          <div
            class="badgebutton active mr-2"
            v-b-tooltip:hover
            title="Comment on this attachment"
            v-on:click.stop="commentOn(attachment)"
            v-if="canComment"
          >
            <i class="far fa-comment-lines"></i>
          </div>
          <div
            class="badgebutton danger active"
            v-b-tooltip:hover
            title="Delete this attachment"
            v-on:click.stop="confirmDelete(attachment)"
            v-if="canDelete"
          >
            <i class="far fa-trash"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import { AttachmentsModule, AttachmentsStore } from '@/store/attachments';
  import FileSaver from 'file-saver';
  import { API_URL } from '@/config';
  import { AttachmentDestination, AttachmentDto, DeleteAttachmentDto, UpdateAttachmentDto } from '@/apiclient/apiclient_generated';
  import { attachmentClient } from '@/apiclient/opsboardapi';

  @Component
  export default class AttachmentView extends Vue {
    @Prop() destination: AttachmentDestination;
    @Prop() attachments: AttachmentDto[];
    @Prop() readOnly: boolean;
    @Prop() destinationId: string;
    @Prop({ default: 'Attachments' }) header: string;
    @Prop({ default: true }) canDelete: boolean;
    @Prop({ default: '*' }) fileExtensions: string;
    @Prop({ default: '' }) ignoreFileExtensions: string;
    @Prop() disableDownloadAll: boolean;

    private apiUrl = API_URL;

    private vvieweroptions = {
      inline: false,
      button: false,
      navbar: false,
      title: false,
      toolbar: false,
      tooltip: false,
      movable: true,
      zoomable: true,
      rotatable: true,
      scalable: true,
      transition: true,
      fullscreen: true,
      keyboard: true,
      url: 'data-src',
    };

    canBeDisplayedInline(attachment: AttachmentDto) {
      return this.imageFileExtensions.some((t) => t == attachment.fileExtension);
    }

    async moveAttachmentToInline(attachment: AttachmentDto) {
      await attachmentClient.updateFile({
        id: attachment.id,
        boardItemId: this.destinationId,
        isWideBoardItemAttachment: true,
      } as UpdateAttachmentDto);
    }

    private imageFileExtensions = ['.jpg', '.jpeg', '.png', '.bmp'];

    private attachmentsStore: AttachmentsModule = AttachmentsStore;

    get showAttachmentsView() {
      switch (this.destination) {
        case AttachmentDestination.BoardItem:
        case AttachmentDestination.SpecialOccurrence:
        case AttachmentDestination.MaterialPart:
          return this.attachments?.length;
        default:
          return false;
      }
    }

    async downloadFile(attachment) {
      if (this.imageFileExtensions.indexOf(attachment.fileExtension) != -1) return;

      const fileData = await this.attachmentsStore.downloadAttachment(attachment.id);
      FileSaver.saveAs(fileData.data, fileData.fileName);
    }

    async downloadAll() {
      const fileData = await attachmentClient.downloadAllAttachments(this.destinationId, this.destination);
      FileSaver.saveAs(fileData.data, fileData.fileName);
    }

    get filteredAttachments(): AttachmentDto[] {
      if (this.fileExtensions == '*' && this.ignoreFileExtensions == '') return this.attachments;

      const fileExtArr = this.fileExtensions.toUpperCase().split('|');
      const fileIgnoreExtArr = this.ignoreFileExtensions.toUpperCase().split('|');

      if (this.fileExtensions != '*') {
        return this.attachments.filter((_) => fileExtArr.indexOf(_.fileExtension.toUpperCase().replace('.', '')) !== -1);
      }

      if (this.ignoreFileExtensions != '') {
        return this.attachments.filter((_) => fileIgnoreExtArr.indexOf(_.fileExtension.toUpperCase().replace('.', '')) === -1);
      }

      return this.attachments;
    }

    confirmDelete(attachment) {
      this.$bvModal
        .msgBoxConfirm('Do you really want to delete the attachment ' + attachment.fileName, {
          title: 'Confirm deletion',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete attachment',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: false,
        })
        .then((value) => {
          if (value) {
            const params = {
              ownerId: '00000000-0000-0000-0000-000000000000',
              attachmentDestination: this.destination,
            } as DeleteAttachmentDto;

            switch (this.destination) {
              case AttachmentDestination.BoardItem:
                params.ownerId = this.destinationId;
                break;
            }

            this.attachmentsStore.deleteAttachment({ id: attachment.id, body: params, destination: this.destination }).then(() => {
              this.$emit('deleted');
            });
          }
        })
        .catch(() => {
          // An error occurred
        });
    }

    get canComment(): boolean {
      return this.destination.toLowerCase() === 'boarditem';
    }

    commentOn(attachment) {
      this.$root.$emit('commentonattachment', attachment);
    }
  }
</script>

<style scoped lang="scss">
  .attachment {
    margin-bottom: 10px;
    cursor: pointer;

    .attachment-thumbnail {
      background: #e5e5e5;
      border-radius: 5px;
      height: 80px;
      width: 112px;
      text-align: center;
      font-weight: bold;
      color: #5e6c84;
      margin-right: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &:hover {
      background: #eaeaea;

      .delete-attachment {
        visibility: visible;
        opacity: 1;
        transition: all 0.3s;
      }
    }

    .delete-attachment {
      visibility: hidden;
      opacity: 0;
      font-size: 0.8rem;
      margin-left: auto;
      margin-right: 10px;
    }
  }

  .attachments {
    .downloadAllButton {
      cursor: pointer;
      font-weight: bold;
      visibility: hidden;
      opacity: 0;
      display: inline-block;
      transition: opacity 0.5s;
    }

    &:hover {
      .downloadAllButton {
        visibility: visible;
        opacity: 1;
      }
    }
  }
</style>
